/**
 * Drawing canvas auto-save queue (Phase 6C).
 *
 * Drafters never click Save. Every primitive edit (place / drag /
 * commit-arrow / etc.) calls `enqueueMutation`, which collapses
 * rapid successive edits into a single batched POST after a
 * 400ms debounce.
 *
 * The queue is per-figure: switching figures does NOT lose the
 * outgoing batch for the previous figure — flushing fires
 * immediately on figure switch so the previous figure's saves
 * land before the new figure's mount fetch.
 *
 * Offline behaviour: when the network call fails (or `navigator.onLine`
 * is false at flush time), the pending mutations are persisted to
 * IndexedDB under store ``drawing_offline_queue`` keyed by matterId.
 * On reload, ``hydratePendingMutations`` reads them back and
 * re-fires ``flushNow``.
 *
 * SEC-F03 / ARCH-F04 (Phase 27 3-G) — storage migrated from
 * localStorage to IndexedDB. Prior implementation called
 * ``localStorage.setItem(key, JSON.stringify(_state.pending))``
 * which threw ``QuotaExceededError`` silently when the ~5-10 MB
 * per-origin localStorage limit was hit. Large figures with many
 * primitives could exhaust the quota during a long drafting
 * session; the catch swallowed the throw and the pending queue
 * silently truncated to the on-disk snapshot from before the
 * write failure. Drafters lost work without any UI signal.
 * IDB has a much larger default budget (50 MB+ per origin), runs
 * async (no main-thread block on big writes), and surfaces
 * failures via the onerror callback so we can toast.
 *
 * The autosave layer does NOT know about Zustand — it's a pure
 * event-bus. The store registers a flushHandler that turns the
 * queued mutations into a ``saveDrawingCanvas`` POST AND a
 * ``setOnPersistFailed`` callback that surfaces a toast when the
 * IDB write rejects.
 */

import {
  saveDrawingCanvas,
  deleteDrawingCanvasPrimitive,
  type CanvasPrimitive,
  type CanvasPrimitiveInput,
} from '@/api/client';

export type CanvasMutation =
  | {
      kind: 'upsert';
      matterId: string;
      figureId: string;
      /** When `id` is set the upsert UPDATEs; otherwise INSERTs. */
      primitive: CanvasPrimitiveInput;
    }
  | {
      kind: 'delete';
      matterId: string;
      figureId: string;
      primitiveId: string;
    }
  | {
      kind: 'replace_all';
      matterId: string;
      figureId: string;
      primitives: CanvasPrimitiveInput[];
    };

export interface QueueState {
  pending: CanvasMutation[];
  flushing: boolean;
  /** Set when the last flush failed and we're holding mutations on
   * disk until reconnect. */
  offline: boolean;
  lastSavedAt: number | null;
}

// BUG-038 — bumped from 400ms → 1500ms after drafters reported a
// rapid-fire POST loop alternating 200 / 400 during active canvas
// interaction. 400ms wasn't enough to coalesce a typical drag burst
// or a multi-primitive paste; 1500ms keeps the canvas responsive
// while collapsing visible work into a single network round-trip
// per user gesture.
export const DEBOUNCE_MS = 1500;

const _state: QueueState = {
  pending: [],
  flushing: false,
  offline: false,
  lastSavedAt: null,
};

let _timer: ReturnType<typeof setTimeout> | null = null;
let _onChange: ((s: Readonly<QueueState>) => void) | null = null;

function _emit(): void {
  if (_onChange) _onChange({ ..._state, pending: [..._state.pending] });
}

export function getQueueState(): Readonly<QueueState> {
  return { ..._state, pending: [..._state.pending] };
}

export function subscribeToQueue(
  fn: (s: Readonly<QueueState>) => void,
): () => void {
  _onChange = fn;
  return () => {
    if (_onChange === fn) _onChange = null;
  };
}

/**
 * Reconciliation hook fired after every successful flush (debounced
 * timer or manual ``flushNow``). The store registers a callback that
 * walks ``appliedPrimitives`` and swaps any ``local-*`` placeholder
 * id in its cache with the backend-assigned real UUID. Without this
 * the debounced auto-save path leaves placeholders stranded — the
 * primitive lands in the DB on first save, but every subsequent
 * edit ships another ``id: undefined`` (because we strip local-*
 * on enqueue) and the backend INSERTs a duplicate row.
 *
 * Single-callback pattern (not multi-subscriber): the store is the
 * only consumer; tests can swap it transiently if they need to
 * intercept.
 */
type FlushAppliedCallback = (
  appliedPrimitives: CanvasPrimitive[],
) => void;
let _onFlushApplied: FlushAppliedCallback | null = null;
export function setOnFlushApplied(fn: FlushAppliedCallback | null): void {
  _onFlushApplied = fn;
}

// ─── SEC-F03 / ARCH-F04 (Phase 27 3-G) — IndexedDB persistence ────
//
// Schema: one object store ``drawing_offline_queue`` keyed by
// matterId. Each value is a JSON-serialisable ``CanvasMutation[]``.
// Mirrors the per-singleton-key pattern from
// ``specOfflineQueue.ts`` (which is the spec module's equivalent).
//
// Quota: IDB's default budget is ~50 MB per origin on most browsers
// (Chrome reserves up to 60% of disk space; Firefox 50% capped at
// 2 GB). Even a multi-hour drafting session with 10k+ pending
// primitives lands well under that ceiling. Quota failures still
// arrive as transaction onerror; the persist callback surfaces a
// toast so the drafter sees the degradation rather than losing
// work silently.

const _DB_NAME = 'claimstudio_drawing';
const _DB_VERSION = 1;
const _STORE_NAME = 'drawing_offline_queue';

function _isIDBAvailable(): boolean {
  return typeof window !== 'undefined'
    && typeof window.indexedDB !== 'undefined';
}

async function _openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_DB_NAME, _DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(_STORE_NAME)) {
        db.createObjectStore(_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

/** Persistence-failure hook. The store wires this to its
 *  ``showToast`` action so a quota / corruption failure surfaces
 *  in the UI instead of silently dropping the queue's tail. */
type PersistFailCallback = (reason: string) => void;
let _onPersistFailed: PersistFailCallback | null = null;
export function setOnPersistFailed(fn: PersistFailCallback | null): void {
  _onPersistFailed = fn;
}

async function _persistOffline(matterId: string): Promise<void> {
  if (!_isIDBAvailable()) return;
  let db: IDBDatabase | null = null;
  try {
    db = await _openDB();
  } catch (e) {
    if (_onPersistFailed) {
      _onPersistFailed(
        'Auto-save storage unavailable — pending changes may not '
        + 'survive a reload. Reload anyway?',
      );
    }
    return;
  }
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(_STORE_NAME, 'readwrite');
      const store = tx.objectStore(_STORE_NAME);
      const req = _state.pending.length === 0
        ? store.delete(matterId)
        : store.put(_state.pending, matterId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    // IDB write failed — typically QuotaExceeded, occasionally
    // VersionError / TransactionInactive. Surface a toast so the
    // drafter knows their pending changes aren't durable on this
    // tab.
    if (_onPersistFailed) {
      const msg = (e as { message?: string })?.message
        || 'Auto-save storage write failed';
      _onPersistFailed(
        `Auto-save failed — check available storage. (${msg})`,
      );
    }
  } finally {
    db.close();
  }
}

async function _readPersisted(matterId: string): Promise<CanvasMutation[]> {
  if (!_isIDBAvailable()) return [];
  let db: IDBDatabase | null = null;
  try {
    db = await _openDB();
  } catch {
    return [];
  }
  try {
    return await new Promise<CanvasMutation[]>((resolve, reject) => {
      const tx = db!.transaction(_STORE_NAME, 'readonly');
      const store = tx.objectStore(_STORE_NAME);
      const req = store.get(matterId);
      req.onsuccess = () => {
        const v = req.result as CanvasMutation[] | undefined;
        resolve(Array.isArray(v) ? v : []);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  } finally {
    db.close();
  }
}

export function enqueueMutation(m: CanvasMutation): void {
  _state.pending.push(m);
  // SEC-F03 (Phase 27 3-G) — fire-and-forget IDB write. The
  // promise resolves microseconds after the put completes; if it
  // rejects (quota / corruption) the persist-failed callback
  // toasts via setOnPersistFailed. We don't await here to keep
  // the enqueue path itself sync (callers fire this from input
  // handlers).
  void _persistOffline(m.matterId);
  _emit();
  // BUG-038 — additional in-flight guard: when a flush is already
  // in progress, do NOT reset the debounce timer. Resetting would
  // either (a) postpone the next flush indefinitely if the user
  // keeps editing during a slow flush, or (b) re-trigger a flush
  // immediately on completion when the user has already stopped.
  // Instead let the in-flight save complete; the next user
  // interaction (or the post-flush emit) will trigger a fresh
  // timer via the standard path.
  if (_state.flushing) return;
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => {
    void flushNow();
  }, DEBOUNCE_MS);
}

/** BUG-038 — client-side shape guard. Drops primitives that would
 * fail the backend's per-type validator (drawing.py:_validate_primitive)
 * before they ship in a save batch. The backend was returning 400
 * on rare malformed primitives (mid-drag pointermove emitting an
 * arrow with x2/y2 = NaN; a numeral chip whose drawing_numeral_id
 * had been cleared by a registry deletion) which then poisoned the
 * autosave queue. The per-batch 4xx drop in flushNow already
 * recovers, but filtering up-front keeps healthy primitives from
 * being dropped alongside the bad one in the same batch.
 *
 * Returns ``[kept, droppedCount]``. ``droppedCount > 0`` is logged
 * to console.warn so dev-tools surface the cause without requiring
 * a network-tab dive. */
function _filterValidPrimitives(
  primitives: CanvasPrimitiveInput[],
): [CanvasPrimitiveInput[], number] {
  const kept: CanvasPrimitiveInput[] = [];
  let dropped = 0;
  for (const p of primitives) {
    if (_isPrimitiveValid(p)) {
      kept.push(p);
    } else {
      dropped += 1;
      if (typeof console !== 'undefined') {
        console.warn(
          '[drawingAutoSave] dropping malformed primitive before save',
          p,
        );
      }
    }
  }
  return [kept, dropped];
}

function _isNum(v: unknown): boolean {
  return typeof v === 'number' && Number.isFinite(v);
}
function _isNonEmptyStr(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

function _isPrimitiveValid(p: CanvasPrimitiveInput): boolean {
  const ptype = p.primitive_type;
  const d = p.primitive_data_json as Record<string, unknown> | undefined;
  if (!d || typeof d !== 'object') return false;
  switch (ptype) {
    case 'numeral':
      // NB-09 — a raster anchor (``in_raster: true``: the numeral is
      // printed in the figure image) has no position. Dropping it
      // here would make a replace_all save delete it server-side.
      return (d.in_raster === true || (_isNum(d.x) && _isNum(d.y)))
        && _isNonEmptyStr(d.numeral) && _isNonEmptyStr(d.label)
        && _isNonEmptyStr(p.drawing_numeral_id);
    case 'arrow':
    case 'curved_arrow':
      return _isNum(d.x1) && _isNum(d.y1)
        && _isNum(d.x2) && _isNum(d.y2);
    case 'text':
      return _isNum(d.x) && _isNum(d.y) && typeof d.text === 'string';
    case 'lasso': {
      const pts = d.points as Array<{ x?: unknown; y?: unknown }> | undefined;
      if (!Array.isArray(pts) || pts.length < 3) return false;
      return pts.every((pt) => _isNum(pt?.x) && _isNum(pt?.y));
    }
    default:
      return false;
  }
}

/**
 * Group consecutive same-figure upsert mutations so a single batched
 * `saveDrawingCanvas` POST goes out per figure. Delete mutations stay
 * per-row (one DELETE call each). `replace_all` mutations bypass
 * grouping — each is its own bulk POST.
 */
function _groupForFlush(
  mutations: CanvasMutation[],
): Array<CanvasMutation | {
  kind: 'upsert_batch';
  matterId: string;
  figureId: string;
  primitives: CanvasPrimitiveInput[];
}> {
  const out: Array<CanvasMutation | {
    kind: 'upsert_batch';
    matterId: string;
    figureId: string;
    primitives: CanvasPrimitiveInput[];
  }> = [];
  for (const m of mutations) {
    if (m.kind === 'upsert') {
      const prev = out[out.length - 1];
      if (
        prev
        && (prev as { kind: string }).kind === 'upsert_batch'
        && (prev as { matterId: string }).matterId === m.matterId
        && (prev as { figureId: string }).figureId === m.figureId
      ) {
        // De-dupe by primitive id within a batch — successive drag
        // events on the same primitive collapse to the latest pos.
        const batch = prev as {
          kind: 'upsert_batch';
          matterId: string;
          figureId: string;
          primitives: CanvasPrimitiveInput[];
        };
        if (m.primitive.id) {
          const idx = batch.primitives.findIndex((p) => p.id === m.primitive.id);
          if (idx >= 0) {
            batch.primitives[idx] = m.primitive;
          } else {
            batch.primitives.push(m.primitive);
          }
        } else {
          batch.primitives.push(m.primitive);
        }
      } else {
        out.push({
          kind: 'upsert_batch',
          matterId: m.matterId,
          figureId: m.figureId,
          primitives: [m.primitive],
        });
      }
    } else {
      out.push(m);
    }
  }
  return out;
}

/**
 * Apply queued mutations against the backend. Called by the debounce
 * timer + on-demand from the store (e.g. on figure switch / blur).
 */
export async function flushNow(): Promise<{
  ok: boolean;
  applied: number;
  appliedPrimitives?: CanvasPrimitive[];
  /** HR-P2.2 — last-seen figure_updated_at per figureId across
   *  the batch. The store stashes these into
   *  figureUpdatedAtCache so the focus-refetch comparison has a
   *  fresh local timestamp to compare against. */
  figureUpdatedAtByFigure?: Record<string, string>;
  /** BUG-033 — count of mutations the backend permanently
   * rejected (4xx) and we dropped from the queue. Caller can
   * surface a single "N changes were rejected" toast. */
  droppedBadCount?: number;
  lastDropError?: string;
}> {
  if (_state.flushing) return { ok: true, applied: 0 };
  if (_state.pending.length === 0) return { ok: true, applied: 0 };

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    _state.offline = true;
    _emit();
    return { ok: false, applied: 0 };
  }

  const batch = _groupForFlush(_state.pending);
  const matterId = _state.pending[0].matterId;
  _state.flushing = true;
  _emit();

  let applied = 0;
  const appliedPrimitives: CanvasPrimitive[] = [];
  const figureUpdatedAtByFigure: Record<string, string> = {};
  let droppedBadCount = 0;
  let lastDropError: string | null = null;
  // BUG-033 — per-batch error handling. The previous catch was at
  // the loop level, so a single 400 (e.g. a malformed primitive)
  // bounced the whole flush and left _state.pending intact;
  // every subsequent flush retried the same bad batch ad
  // infinitum, which is how the user's queue grew to 23 040
  // mutations. We now classify per batch:
  //   * 4xx (other than 429) — permanent client error. Drop the
  //     batch's source mutations + record the reason so the UI
  //     can surface a single "N changes were rejected" toast.
  //     Other batches in the same flush still get a chance.
  //   * 5xx / network / 429 — transient. Abort, stay offline,
  //     keep pending for the next flush.
  // Errors are accumulated; the loop stops on the FIRST transient
  // failure (so we don't burn requests in a known-bad state) but
  // continues past 4xx drops.
  try {
    let stopAfterTransient = false;
    for (const op of batch) {
      if (stopAfterTransient) break;
      try {
        if (op.kind === 'upsert_batch') {
          // BUG-038 — drop malformed primitives before send. Bad
          // primitives still come out of the queue (the matching
          // _dropMutationsForBatch call below removes their source
          // mutations either way), so they don't loop.
          const [validPrims, droppedBad] = _filterValidPrimitives(op.primitives);
          if (droppedBad > 0) droppedBadCount += droppedBad;
          if (validPrims.length === 0) {
            // Nothing valid in this batch — still drop the source
            // mutations so the queue doesn't keep retrying.
            _state.pending = _dropMutationsForBatch(_state.pending, op);
            continue;
          }
          const r = await saveDrawingCanvas(
            op.matterId, op.figureId, validPrims, false,
          );
          applied += validPrims.length;
          appliedPrimitives.push(...r.primitives);
          if (r.figure_updated_at) {
            figureUpdatedAtByFigure[op.figureId] = r.figure_updated_at;
          }
          _state.pending = _dropMutationsForBatch(_state.pending, op);
        } else if (op.kind === 'replace_all') {
          const [validPrims, droppedBad] = _filterValidPrimitives(op.primitives);
          if (droppedBad > 0) droppedBadCount += droppedBad;
          const r = await saveDrawingCanvas(
            op.matterId, op.figureId, validPrims, true,
          );
          applied += validPrims.length;
          appliedPrimitives.push(...r.primitives);
          if (r.figure_updated_at) {
            figureUpdatedAtByFigure[op.figureId] = r.figure_updated_at;
          }
          _state.pending = _dropMutationsForBatch(_state.pending, op);
        } else if (op.kind === 'delete') {
          await deleteDrawingCanvasPrimitive(
            op.matterId, op.figureId, op.primitiveId,
          );
          applied += 1;
          _state.pending = _dropMutationsForBatch(_state.pending, op);
        }
      } catch (err) {
        const status = (err as { response?: { status?: number } })
          ?.response?.status;
        const isTransient = !status || status === 429 || status >= 500;
        if (isTransient) {
          stopAfterTransient = true;
        } else {
          // Permanent 4xx — drop the source mutations from
          // _state.pending so they don't loop forever.
          const beforeLen = _state.pending.length;
          _state.pending = _dropMutationsForBatch(_state.pending, op);
          droppedBadCount += beforeLen - _state.pending.length;
          const detail = (err as { response?: { data?: { detail?: unknown } } })
            ?.response?.data?.detail;
          lastDropError = typeof detail === 'string'
            ? detail
            : (err as { message?: string })?.message || `HTTP ${status}`;
        }
      }
    }
    if (_state.pending.length === 0) {
      _state.offline = false;
      _state.lastSavedAt = Date.now();
    } else {
      // Anything left in pending is by definition a transient
      // failure (4xx batches were dropped above). Mark offline so
      // the indicator + drain-on-online path do their job.
      _state.offline = true;
    }
    // SEC-F03 (Phase 27 3-G) — await so post-flush state is
    // durable before the function returns. The caller may rely
    // on the flush having landed on disk before navigating away.
    await _persistOffline(matterId);
    _emit();
    // Fire the reconciler so the store can swap any ``local-*``
    // placeholder ids in its cache with the real UUIDs the backend
    // just assigned. Runs for both debounced and manual
    // (``flushCanvasNow``) flushes — manual callers ALSO consume
    // ``appliedPrimitives`` from the return value, but the
    // callback path is the only thing that reaches the
    // debounced-flush case (the timer at line 141 discards the
    // return). The callback is allowed to throw; we swallow + log
    // so a buggy reconciler can't break the queue lifecycle.
    if (_onFlushApplied && appliedPrimitives.length > 0) {
      try { _onFlushApplied(appliedPrimitives); }
      catch (e) {
        if (typeof console !== 'undefined') {
          console.warn('[drawingAutoSave] onFlushApplied threw', e);
        }
      }
    }
    return {
      ok: _state.pending.length === 0,
      applied,
      appliedPrimitives,
      figureUpdatedAtByFigure,
      droppedBadCount: droppedBadCount > 0 ? droppedBadCount : undefined,
      lastDropError: lastDropError || undefined,
    };
  } finally {
    _state.flushing = false;
    _emit();
  }
}

/** BUG-033 — drop the mutations corresponding to a successfully-
 * applied or permanently-rejected batch from _state.pending. We
 * match by matterId + figureId + primitive id (or primitive
 * identity for adds without an id yet). Conservative — only
 * removes the exact mutations the batch consumed. */
function _dropMutationsForBatch(
  pending: CanvasMutation[],
  op: CanvasMutation | {
    kind: 'upsert_batch';
    matterId: string;
    figureId: string;
    primitives: CanvasPrimitiveInput[];
  },
): CanvasMutation[] {
  if ('kind' in op && op.kind === 'delete') {
    return pending.filter((m) =>
      !(m.kind === 'delete'
        && m.matterId === op.matterId
        && m.figureId === op.figureId
        && m.primitiveId === op.primitiveId),
    );
  }
  if ('kind' in op && op.kind === 'replace_all') {
    return pending.filter((m) =>
      !(m.kind === 'replace_all'
        && m.matterId === op.matterId
        && m.figureId === op.figureId),
    );
  }
  if ('kind' in op && op.kind === 'upsert_batch') {
    const ids = new Set<string>();
    op.primitives.forEach((p) => { if (p.id) ids.add(p.id); });
    let unidentifiedRemaining = op.primitives.filter((p) => !p.id).length;
    return pending.filter((m) => {
      if (m.kind !== 'upsert') return true;
      if (m.matterId !== op.matterId) return true;
      if (m.figureId !== op.figureId) return true;
      if (m.primitive.id && ids.has(m.primitive.id)) return false;
      if (!m.primitive.id && unidentifiedRemaining > 0) {
        unidentifiedRemaining -= 1;
        return false;
      }
      return true;
    });
  }
  return pending;
}

/** Cancel any pending debounce — used when the matter or figure
 * unmounts so a stale flush doesn't fire after navigation. */
export function cancelPendingFlush(): void {
  if (_timer) {
    clearTimeout(_timer);
    _timer = null;
  }
}

/** Read the persisted mutations from a previous session and re-fire
 * the queue. Called once on `DrawingPage` mount per matter. Returns
 * the count of restored mutations so the page can surface a toast. */
export async function hydratePendingMutations(matterId: string): Promise<number> {
  if (typeof window === 'undefined') return 0;
  // SEC-F03 (Phase 27 3-G) — async IDB read. Fails open (returns
  // empty array) when IDB is unavailable or the read rejects, so
  // hydration on a fresh device matches the pre-migration
  // behaviour.
  const persisted = await _readPersisted(matterId);
  if (persisted.length === 0) return 0;
  _state.pending.push(...persisted);
  _emit();
  if (_timer) clearTimeout(_timer);
  // Fire a flush immediately on hydration; the debounce is for
  // collapsing burst input, not for first-load sync.
  void flushNow();
  return persisted.length;
}

/** BUG-033 — emergency drain. Wipes the in-memory + persisted
 * queue without trying to flush. Used when a deploy-time schema
 * change leaves a queue full of permanently-rejected mutations
 * (the fix path also drops 4xx batches automatically, so this
 * is mostly a manual escape hatch for already-stuck queues). */
export function discardPendingMutations(matterId: string): number {
  const dropped = _state.pending.length;
  _state.pending = [];
  _state.offline = false;
  if (_timer) { clearTimeout(_timer); _timer = null; }
  // Fire-and-forget IDB clear. Same fire-and-forget semantics as
  // enqueueMutation — the caller wants a sync count back; the
  // disk wipe can race the return without consequence.
  void _persistOffline(matterId);
  _emit();
  return dropped;
}

/** Test seam — the client-side save-shape guard (NB-09 regression). */
export const _isPrimitiveValidForTests = _isPrimitiveValid;

/** Test/debug seam — wipe every in-memory queue field and the
 * matter's IDB entry. Not used in production code. */
export function _resetForTests(matterId?: string): void {
  _state.pending = [];
  _state.flushing = false;
  _state.offline = false;
  _state.lastSavedAt = null;
  if (_timer) {
    clearTimeout(_timer);
    _timer = null;
  }
  // SEC-F03 (Phase 27 3-G) — wipe IDB entry (was localStorage).
  // Fire-and-forget; tests that need to assert post-reset state
  // should await ``hydratePendingMutations`` which returns 0
  // when the store is empty.
  if (matterId) {
    void _persistOffline(matterId);
  }
  _emit();
}
